import { useState } from "react";
import { CloudUpload, Upload, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useFileUpload } from "./useFileUpload";

const DEFAULT_ACCEPT = ".pdf,.csv,.xlsx,.xls,.png,.jpg,.jpeg,.webp,.gif";

const FileUploadDropzone = ({
  label,
  value,
  onChange,
  accept = DEFAULT_ACCEPT,
  baseUrl = "",
  compact = false,
  className,
  labelClassName,
  frameClassName,
  disabled = false,
}) => {
  const {
    inputRef,
    effectiveUrl,
    displayName,
    isImage,
    hasAnyFile,
    handleFile,
    onBrowseClick,
  } = useFileUpload({ value, onChange, baseUrl });
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    if (disabled) return;
    const file = event.dataTransfer.files?.[0] ?? null;
    if (file) handleFile(file);
  };

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <Label className={cn(compact ? "text-sm" : "text-xs", labelClassName)}>
          {label}
        </Label>
      )}

      <div
        className={cn(
          "relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed text-center transition-colors",
          compact ? "min-h-0 gap-1.5 px-1 py-2" : "min-h-[11rem] gap-3 p-6",
          isDragOver ? "border-primary bg-primary/5" : "border-border",
          !hasAnyFile && "bg-muted/20",
          frameClassName,
        )}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
      >
        {hasAnyFile && isImage && effectiveUrl ? (
          <>
            <img
              src={effectiveUrl}
              alt={displayName || "Preview"}
              className="max-h-40 w-full rounded-md object-contain"
            />
            <div className="absolute right-2 top-2 flex gap-1">
              <button
                type="button"
                onClick={onBrowseClick}
                title="Replace"
                disabled={disabled}
                className="rounded-md bg-background/90 p-1.5 shadow hover:bg-muted"
              >
                <Upload className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => onChange?.(null)}
                title="Remove"
                disabled={disabled}
                className="rounded-md bg-background/90 p-1.5 text-destructive shadow hover:bg-muted"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </>
        ) : hasAnyFile ? (
          <>
            <div className="max-w-full truncate text-sm font-medium text-foreground">
              {displayName || "File"}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                onClick={onBrowseClick}
                disabled={disabled}
              >
                Replace
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onChange?.(null)}
                disabled={disabled}
              >
                Remove
              </Button>
            </div>
          </>
        ) : (
          <>
            <div
              className={cn(
                "flex items-center justify-center rounded-full bg-primary/10 text-primary",
                compact ? "h-8 w-8" : "h-12 w-12",
              )}
            >
              <CloudUpload className={compact ? "h-4 w-4" : "h-6 w-6"} />
            </div>
            <p
              className={cn(
                "text-muted-foreground",
                compact ? "text-xs leading-tight" : "text-sm",
              )}
            >
              Drag & Drop your files here
            </p>
            <p
              className={cn(
                "font-medium text-muted-foreground",
                compact ? "text-[10px] leading-none" : "text-xs",
              )}
            >
              OR
            </p>
            <Button
              type="button"
              size={compact ? "xs" : "sm"}
              onClick={onBrowseClick}
              disabled={disabled}
            >
              Browse Files
            </Button>
          </>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        disabled={disabled}
        accept={accept}
        onChange={(event) => {
          const file =
            event.target.files && event.target.files[0]
              ? event.target.files[0]
              : null;
          if (file) handleFile(file);
          if (inputRef.current) inputRef.current.value = "";
        }}
      />
    </div>
  );
};

export default FileUploadDropzone;
